import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Observable, switchMap, map, withLatestFrom } from 'rxjs';
import { CrmAssociationService } from '../../../shared-services';
import { CrmObjectAssociationFormField, FormFieldAssociation } from '../interface';
import { CrmAssociationFormFieldComponent } from '../association-form-field.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
  selector: 'crm-contact-to-company-association-form-field',
  templateUrl: './contact-to-company-association.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, CrmAssociationFormFieldComponent],
})
export class CrmContactToCompanyAssociationFormFieldComponent extends CrmObjectAssociationFormField {
  @Input() set namespace(n: string) {
    this.setNamespace(n);
  }
  @Input({ required: true }) set associations(value: FormFieldAssociation[]) {
    this.setAssociations(value);
  }
  @Input() fixedId: string | null = null;
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input() placeholder = '';

  @Output() associationsChange = new EventEmitter<FormFieldAssociation[]>();
  protected readonly crmObjectType = 'Contact';
  protected readonly crmAssociationService = inject(CrmAssociationService);

  protected handleAdd(association: FormFieldAssociation): void {
    const associations = this.associationsList();
    if (!associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      const newAssociations = [...associations, association];
      this.associationsList.set(newAssociations);
      this.associationsChange.emit(newAssociations);
    }
  }

  protected handleRemove(association: FormFieldAssociation): void {
    const associations = this.associationsList().filter((a) => a.crmObjectId !== association.crmObjectId);
    this.associationsList.set(associations);
    this.associationsChange.emit(associations);
  }

  protected handleSearchTermChange(searchTerm?: unknown): void {
    this.updateSearchTerm(searchTerm);
  }

  protected override setupSearchTerm(): Observable<FormFieldAssociation[]> {
    return super.setupSearchTerm().pipe(
      withLatestFrom(this.searchTerm$$.asObservable()),
      switchMap((values: [FormFieldAssociation[], string]) => {
        let allAssociations: FormFieldAssociation[] = values[0];
        const searchTerm: string = values[1];
        return this.crmAssociationService.getCachedContactAssociationsForCompany$(this.fixedId || '').pipe(
          map((contacts) => {
            let associatedContacts =
              contacts
                .filter((c) => c.associatedObject)
                .map(
                  (contact) =>
                    ({
                      name: this.customizationService.getObjectDisplayName('Contact', contact.associatedObject),
                      associationInfo: this.customizationService.getAssociationInfo(
                        'Contact',
                        contact.associatedObject,
                      ),
                      crmObjectId: contact.associatedObject.crmObjectId,
                      crmObjectType: this.crmObjectType,
                    }) as FormFieldAssociation,
                ) || [];
            associatedContacts = associatedContacts.filter((c) => contactContains(c, searchTerm));
            allAssociations = allAssociations.filter((a) => !contactIncludes(associatedContacts, a));
            if (associatedContacts.length > 10) {
              associatedContacts = associatedContacts.slice(0, 10);
            }
            return associatedContacts.concat(allAssociations);
          }),
        ) as Observable<FormFieldAssociation[]>;
      }),
    );
  }
}

function contactContains(contact: FormFieldAssociation, search: string): boolean {
  if (!search || search === '') return true;
  return (
    contact.name?.toLowerCase().includes(search.toLowerCase()) ||
    contact.associationInfo?.toLowerCase().includes(search.toLowerCase()) ||
    false
  );
}

function contactIncludes(array: FormFieldAssociation[], searchElement: FormFieldAssociation, fromIndex = 0): boolean {
  const len = array.length;
  const startIndex = fromIndex < 0 ? Math.max(0, len + fromIndex) : fromIndex;

  for (let i = startIndex; i < len; i++) {
    const currentElement = array[i];
    if (searchElement.crmObjectId === currentElement.crmObjectId) {
      return true;
    }
    if (searchElement === currentElement) {
      return true; // Handles NaN case
    }
  }

  return false;
}
